import React, {useContext, useEffect, useMemo, useState} from "react";
import {useHistory} from "react-router";
import APIContext from "context/APIContext";
import AuthContext from "context/AuthContext";
import moment from "moment";
import _ from "lodash";
import ShowIf from "components/common/ShowIf";
import {CircularProgress, Grid, Tooltip} from "@material-ui/core";
import {FormTab, FormTabs} from "components/common/FormTabs";
import SubscriptionFlow from "scenes/SubscriptionPage/SubscriptionFlow";
import PlanCard from "scenes/SubscriptionPage/PlanCard";
import {SUBSCRIPTION_STATUS} from "scenes/SubscriptionPage/index";
import CacheContext from "context/CacheContext";
import MyButton from "components/Controls/MyButton";

const getSubscription = "getSubscription";
const unsubscribe = 'unsubscribe';
const retryPayment = 'retryPayment';

const highlightedName = "Pro";

export const FREE_PLAN_ID = 'free';

export const SEARCH_RESULTS_MESSAGE = "Upgrade your subscription to get hundreds of results and experience Ludo's full capabilities!";
export const FEATURE_UNAVAILABLE_MESSAGE = "Upgrade your subscription to access to all features and experience Ludo's full capabilities!"

const Plans = ({onSuccess, redirectOnSubscription, sizes}) => {

  const history = useHistory();
  const {cache} = useContext(CacheContext);
  const {call, loading, loggedIn} = useContext(APIContext);
  const {auth, setAuth} = useContext(AuthContext);

  const {subscription} = auth;
  const {plans} = cache;

  const [monthly, setMonthly] = useState(subscription?.plan?.interval === "month");
  const [selectedPlan, setSelectedPlan] = useState(undefined);
  const [loadingSubscription, setLoadingSubscription] = useState(true);

  let plan = (cache.plans || []).find(plan => !!plan.percent_off);
  const hasDiscount = !!plan?.percent_off;

  const subscriptionStatus = subscription?.status || SUBSCRIPTION_STATUS.unsubscribed;

  useEffect(() => {
    if (redirectOnSubscription &&
      (subscriptionStatus === SUBSCRIPTION_STATUS.active ||
        subscriptionStatus === SUBSCRIPTION_STATUS.trialing))
      history.push('/')
  }, [redirectOnSubscription, subscriptionStatus]);

  useEffect(() => {
    if(loggedIn) {
      call(getSubscription).then(response => {
        if (response.ok) {
          setAuth({subscription: response.body}, true, false);
        }
        setLoadingSubscription(false);
      });
    }
  }, [loggedIn]);

  const freePlan = (plans || []).find(plan => plan.amount === 0);
  const isLoading = loadingSubscription || plans?.length === 0;

  const periodPlans = useMemo(() => {
    let modifiedPlans = (plans || []).map(plan => {
      return {
        ...plan,
        subscribed: subscription.plan.name === plan.name,
        highlight: plan.name === highlightedName,
        amount: (plan.amount || 0) / 100.0
      }
    });
    let sorted = _.sortBy(modifiedPlans.filter(plan => plan.interval === (monthly ? 'month' : 'year')), ['amount']);

    return [...sorted];
  }, [plans, monthly, subscription]);

  async function clickedUnsubscribe() {
    let response = await call(unsubscribe, undefined, {successMessage: 'Your subscription has been scheduled for unsubscription at the end of the current billing period.'});
    if (response.ok) {
      let subscription = response.body;
      setAuth({subscription}, true, false);
    }
  }

  let subscriptionStatusString = undefined;
  const interval = subscription?.plan?.interval;
  const trialEnd = moment.unix(subscription?.trial_end).format('MMMM Do YYYY');

  const planName = `${interval ? `${interval}ly ` : ""}${subscription?.plan?.name}`;

  let renewalInfo = "";
  if (!!subscription.cancel_at) {
    renewalInfo = ` It is scheduled to end on ${moment.unix(subscription.cancel_at).format('MMMM Do YYYY')} and will not be renewed.`;
  } else if (!!subscription.current_period_end) {
    renewalInfo = ` It is scheduled to renew on ${moment.unix(subscription.current_period_end).format('MMMM Do YYYY')}.`;
  }

  async function onRetry() {
    let response = await call(retryPayment, undefined, {successMessage: 'Your payment has been successful!'});
    if (response.ok) {
      let subscription = response.body;
      setAuth({subscription}, true, false);
    }
  }

  switch (subscriptionStatus) {
    case SUBSCRIPTION_STATUS.active:
      subscriptionStatusString =
        <span
          className="subscription-status">Your <strong>{planName}</strong>&nbsp;subscription is active!{renewalInfo}</span>
      break;
    case SUBSCRIPTION_STATUS.trialing:
      subscriptionStatusString =
        <span>
          Your&nbsp;<strong>{planName}</strong>&nbsp;trial is active until&nbsp;
          <strong>{trialEnd}</strong>.{renewalInfo}
        </span>
      break;
    case SUBSCRIPTION_STATUS.past_due:
      subscriptionStatusString =
        <>
          <span>Your&nbsp;<strong>{planName}</strong>&nbsp;subscription is past due. Please
                update your payment details.</span>
          <div className="retry">
            <MyButton loading={loading[unsubscribe]} className="red mx-2" onClick={clickedUnsubscribe}>
              Unsubscribe
            </MyButton>
            <MyButton loading={loading[retryPayment]} className="secondary mx-2" onClick={onRetry}>
              Retry Payment
            </MyButton>
          </div>
        </>
      break;
    case SUBSCRIPTION_STATUS.unsubscribed:
      subscriptionStatusString =
        <span>You are on the <strong>{freePlan?.name}</strong> plan.</span>
      break;
  }

  if(subscriptionStatus === SUBSCRIPTION_STATUS.past_due) {
    subscriptionStatusString = "Your subscription status is past due. Please update your payment details to continue using Ludo.";
  } else if(cache.trialExpired) {
    subscriptionStatusString = "Your Ludo trial expired and you don't currently have a Ludo subscription. Choose one below to continue using Ludo.";
  } else if(isFreePlan(subscription.plan)){
    subscriptionStatusString = "You are currently using the Ludo free plan.";
  }

  return (
    <div className="subscriptions">
      <ShowIf condition={!!selectedPlan}>
        <SubscriptionFlow
          plan={selectedPlan}
          onSuccess={onSuccess}
          goBack={() => setSelectedPlan(undefined)}
        />
      </ShowIf>
      <ShowIf condition={!selectedPlan}>
        <ShowIf condition={isLoading}>
          <div className="text-align-center mt-4 pt-4">
            <CircularProgress size={55}/>
          </div>
        </ShowIf>
        <ShowIf condition={!isLoading}>
          <Grid container className="text-center mt-4">
            <Grid item md={12}>
              {subscriptionStatusString}
            </Grid>
          </Grid>
          <div className="text-align-center w-100 my-5">
            <FormTabs value={monthly ? 1 : 0} className="interval-tabs" centered>
              <FormTab label={
                <Tooltip
                  PopperProps={{
                    disablePortal: true,
                    className: "MuiTooltip-popper MuiTooltip-popperArrow secondary"
                  }}
                  arrow title="Save 20%"
                  className="secondary"
                  placement="top"
                  open={!hasDiscount}
                >
                  <span>Annual</span>
                </Tooltip>
              } onClick={() => setMonthly(false)}/>
              <FormTab label="Monthly" onClick={() => setMonthly(true)}/>
            </FormTabs>
          </div>
          <Grid
            container
            spacing={2}
            justifyContent="center"
            className="d-flex plans-wrapper"
          >
            {periodPlans.map((plan, index) => {
              return (<Grid item {...sizes}>
                <PlanCard
                  key={plan.id || index}
                  plan={plan}
                  onSelectPlan={isFreePlan(plan) ? clickedUnsubscribe : setSelectedPlan}
                  onSuccess={onSuccess}
                  confirmText={isFreePlan(plan) ? "Are you sure you want to downgrade to the Free plan?" : undefined}
                />
              </Grid>);
            })}
          </Grid>
        </ShowIf>
      </ShowIf>
    </div>
  )
};

export function isFreePlan(plan = {}, subscription = {}) {
  return plan.id === FREE_PLAN_ID || subscription?.status === SUBSCRIPTION_STATUS.past_due;
}

export function isTeamsPlan(subscription = {}) {
  return subscription.plan?.metadata?.team === "true";
}

export default Plans;
